import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import { components, GroupBase, OptionsOrGroups, SingleValueProps } from "react-select";
import { DashboardUtils } from "../../dashboard_utils";
import { trpc } from "../../utils/trpc";
import { useChannels } from "./hooks/use_channels";
import { useVoiceChannels } from "./hooks/use_voice_channels";
import { dropdownColourStyles } from "../searchable_dropdown";

interface UserLocation {
	latitude: number;
	longitude: number;
}

interface LocationFieldProps {
	value: string | null;
	onChange: (value: string | null) => void;
	disabled?: boolean;
}

interface LocationOption {
	type: "literal" | "channel" | "place";
	value: string;
	label: string;
	icon: string;
	channelId?: string;
	placeId?: string;
	channelType?: "text" | "voice" | "stage" | "thread";
}

function getChannelIcon(channelType: LocationOption["channelType"]): string {
	switch (channelType) {
		case "voice":
			return "fa-volume-up";
		case "stage":
			return "fa-podcast";
		case "thread":
			return "fa-comment-alt";
		case "text":
		default:
			return "fa-hashtag";
	}
}

interface PlaceLocationData {
	type: "place";
	placeId: string;
	name: string;
	address: string;
}

function parsePlaceDescription(description: string): { name: string; address: string } {
	// Place descriptions are typically "Name, Address, City, State, Country"
	// We'll treat the first part as the name and the rest as the address
	const commaIndex = description.indexOf(",");
	if (commaIndex === -1) {
		return { name: description, address: "" };
	}
	return {
		name: description.slice(0, commaIndex).trim(),
		address: description.slice(commaIndex + 1).trim(),
	};
}

function createPlaceValue(placeId: string, description: string): string {
	const { name, address } = parsePlaceDescription(description);
	const data: PlaceLocationData = {
		type: "place",
		placeId,
		name,
		address,
	};
	return JSON.stringify(data);
}

function parsePlaceValue(value: string): PlaceLocationData | null {
	try {
		const parsed = JSON.parse(value);
		if (parsed && parsed.type === "place" && parsed.placeId) {
			return parsed as PlaceLocationData;
		}
	} catch {
		// Not JSON or not a place
	}
	return null;
}

function LocationOptionLabel(props: { option: LocationOption; isSelected?: boolean }) {
	const { option, isSelected } = props;

	// When selected and it's a channel, show with special styling
	if (isSelected && option.type === "channel") {
		return (
			<span
				className="location-channel-tag"
				style={{
					display: "inline-flex",
					alignItems: "center",
					gap: "4px",
					backgroundColor: "rgba(88, 101, 242, 0.15)",
					color: "#5865f2",
					padding: "2px 8px",
					borderRadius: "4px",
					fontWeight: 500,
				}}
			>
				<i className={`fas ${option.icon}`} style={{ fontSize: "0.85em" }}></i>
				<span>{option.label}</span>
			</span>
		);
	}

	return (
		<div className="columns is-mobile is-vcentered is-variable is-1">
			<div className="column is-narrow">
				<span className="icon">
					<i className={`fas ${option.icon}`}></i>
				</span>
			</div>
			<div className="column">
				<span>{option.label}</span>
			</div>
			<div className="column is-narrow">
				<span className="tag is-small is-light">
					{option.type === "channel" ? "Channel" : option.type === "place" ? "Place" : "Custom"}
				</span>
			</div>
		</div>
	);
}

function LocationSingleValue(props: SingleValueProps<LocationOption>) {
	const { data } = props;

	// For channels, render with special styling
	if (data.type === "channel") {
		return (
			<components.SingleValue {...props}>
				<span
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: "4px",
						backgroundColor: "rgba(88, 101, 242, 0.15)",
						color: "#5865f2",
						padding: "2px 8px",
						borderRadius: "4px",
						fontWeight: 500,
					}}
				>
					<i className={`fas ${data.icon}`} style={{ fontSize: "0.85em" }}></i>
					<span>{data.label}</span>
				</span>
			</components.SingleValue>
		);
	}

	// For other types, just show the label
	return (
		<components.SingleValue {...props}>
			{data.label}
		</components.SingleValue>
	);
}

export function LocationField(props: LocationFieldProps) {
	const [isExpanded, setIsExpanded] = useState(Boolean(props.value));
	const [inputValue, setInputValue] = useState("");
	const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
	const searchCache = useRef<Record<string, LocationOption[]>>({});

	const textChannels = useChannels();
	const voiceChannels = useVoiceChannels();

	const trpcContext = trpc.useContext();

	// Request user's location when field is expanded (for better place suggestions)
	useEffect(() => {
		if (!isExpanded) return;
		if (!navigator.geolocation) return;
		if (userLocation) return; // Already have location

		navigator.geolocation.getCurrentPosition(
			(position) => {
				setUserLocation({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude,
				});
			},
			() => {
				// Silently fail - location is optional
			},
			{
				enableHighAccuracy: false,
				timeout: 5000,
				maximumAge: 600000, // Cache for 10 minutes
			}
		);
	}, [isExpanded]);

	const loadOptions = useCallback(
		debounce(
			(
				input: string,
				callback: (
					options: OptionsOrGroups<
						LocationOption,
						GroupBase<LocationOption>
					>
				) => void
			) => {
				if (!input || input.trim().length === 0) {
					callback([]);
					return;
				}

				const cacheKey = input.toLowerCase();
				if (searchCache.current[cacheKey]) {
					callback(searchCache.current[cacheKey]);
					return;
				}

				const options: LocationOption[] = [];

				// 1. Literal text option (always first)
				options.push({
					type: "literal",
					value: input,
					label: input,
					icon: "fa-keyboard",
				});

				// 2. Filter text channels
				const inputLower = input.toLowerCase();
				if (textChannels) {
					const matchingTextChannels = textChannels
						.filter((channel) =>
							channel.name.toLowerCase().includes(inputLower)
						)
						.slice(0, 3);

					for (const channel of matchingTextChannels) {
						const channelType =
							channel.type === "Thread" ? "thread" : "text";
						options.push({
							type: "channel",
							value: `<#${channel.id}>`,
							label: channel.name,
							icon: getChannelIcon(channelType),
							channelId: channel.id,
							channelType,
						});
					}
				}

				// 3. Filter voice channels
				if (voiceChannels) {
					const matchingVoiceChannels = voiceChannels
						.filter((channel) =>
							channel.name.toLowerCase().includes(inputLower)
						)
						.slice(0, 3);

					for (const channel of matchingVoiceChannels) {
						const channelType = channel.is_stage ? "stage" : "voice";
						options.push({
							type: "channel",
							value: `<#${channel.id}>`,
							label: channel.name,
							icon: getChannelIcon(channelType),
							channelId: channel.id,
							channelType,
						});
					}
				}

				// 4. Fetch Google Places suggestions (only if query >= 3 chars)
				if (input.length >= 3) {
					trpcContext.places.autocomplete
						.fetch({
							query: input,
							latitude: userLocation?.latitude,
							longitude: userLocation?.longitude,
						})
						.then((places) => {
							const placeOptions: LocationOption[] = places
								.slice(0, 5)
								.map((place) => ({
									type: "place" as const,
									value: createPlaceValue(place.placeId, place.description),
									label: place.description,
									icon: "fa-map-marker-alt",
									placeId: place.placeId,
								}));

							const allOptions = [...options, ...placeOptions];
							searchCache.current[cacheKey] = allOptions;
							callback(allOptions);
						})
						.catch(() => {
							// On error, just return channel options
							searchCache.current[cacheKey] = options;
							callback(options);
						});
				} else {
					searchCache.current[cacheKey] = options;
					callback(options);
				}
			},
			300
		),
		[textChannels, voiceChannels, trpcContext, userLocation]
	);

	const handleChange = (option: LocationOption | null) => {
		if (option) {
			props.onChange(option.value);
		} else {
			props.onChange(null);
		}
	};

	// Convert current value to an option for display
	// If value is a channel mention (<#id>), look up the channel to get proper display
	const currentOption: LocationOption | null = useMemo(() => {
		if (!props.value) return null;

		// Check if value is place JSON
		const placeData = parsePlaceValue(props.value);
		if (placeData) {
			const displayLabel = placeData.address
				? `${placeData.name}, ${placeData.address}`
				: placeData.name;
			return {
				type: "place",
				value: props.value,
				label: displayLabel,
				icon: "fa-map-marker-alt",
				placeId: placeData.placeId,
			};
		}

		// Check if value is a channel mention format <#channelId>
		const channelMatch = props.value.match(/^<#(\d+)>$/);
		if (channelMatch) {
			const channelId = channelMatch[1];

			// Look up in text channels
			const textChannel = textChannels?.find((c) => c.id === channelId);
			if (textChannel) {
				const channelType = textChannel.type === "Thread" ? "thread" : "text";
				return {
					type: "channel",
					value: props.value,
					label: textChannel.name,
					icon: getChannelIcon(channelType),
					channelId,
					channelType,
				};
			}

			// Look up in voice channels
			const voiceChannel = voiceChannels?.find((c) => c.id === channelId);
			if (voiceChannel) {
				const channelType = voiceChannel.is_stage ? "stage" : "voice";
				return {
					type: "channel",
					value: props.value,
					label: voiceChannel.name,
					icon: getChannelIcon(channelType),
					channelId,
					channelType,
				};
			}

			// Channel not found in cache - show raw value
			return {
				type: "literal",
				value: props.value,
				label: props.value,
				icon: "fa-keyboard",
			};
		}

		// Regular text value
		return {
			type: "literal",
			value: props.value,
			label: props.value,
			icon: "fa-keyboard",
		};
	}, [props.value, textChannels, voiceChannels]);

	return (
		<div className="field location-field">
			{!isExpanded ? (
				<button
					type="button"
					className="button is-text add-field-button"
					onClick={() => setIsExpanded(true)}
					disabled={props.disabled}
				>
					<span className="icon">
						<i className="fas fa-map-marker-alt"></i>
					</span>
					<span className="add-field-text">Add Location</span>
				</button>
			) : (
				<>
					{DashboardUtils.CreateHeaderAndInfoButton(
						<span className="field-header-with-icon">
							<i className="fas fa-map-marker-alt"></i>
							Location
						</span>,
						"",
						false,
						false,
						false,
						undefined,
						props.disabled
					)}
					<div className="control">
						<AsyncSelect<LocationOption, false>
							classNamePrefix="sesh-dropdown"
							styles={dropdownColourStyles}
							placeholder="ex: DnD Club, 123 Main St"
							isClearable
							value={currentOption}
							inputValue={inputValue}
							onInputChange={(newValue) => setInputValue(newValue)}
							onChange={handleChange}
							loadOptions={loadOptions}
							defaultOptions={false}
							isDisabled={props.disabled}
							components={{ SingleValue: LocationSingleValue }}
							formatOptionLabel={(option) => (
								<LocationOptionLabel option={option} />
							)}
							noOptionsMessage={({ inputValue }) =>
								inputValue
									? "Type to search..."
									: "Start typing to search"
							}
						/>
					</div>
				</>
			)}
		</div>
	);
}
